import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, useWatch, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { DealPhotoGallery } from '@/components/DealPhotoGallery/DealPhotoGallery';
import { DealPhotoUploader } from '@/components/DealPhotoUploader/DealPhotoUploader';
import { HtmlEditor } from '@/components/HtmlEditor';
import '@/styles/admin-emails.css';
import { LocationCombobox, type LocationOption } from '@/components/LocationCombobox';
import { Select, type SelectOption } from '@/components/Select/Select';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import { Checkbox } from '@/components/Checkbox';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useFormDirty } from '@/hooks/useFormDirty';
import {
  isExpirationPast,
  publishDeal,
  republishDeal,
  restoreDeal,
  softDeleteDeal,
  toggleDealFeatured,
  transitionDealStatus,
  updateAdminDeal,
  type AdminDeal,
  type AdminDealStatus,
} from '@/lib/db/admin/deals';
import { retryDealPhotoCopy } from '@/lib/db/admin/deal-photos';
import { diagnoseDealIdentity, type DealIdentityDiagnostic } from '@/lib/db/admin/dld-diagnostics';
import { searchDldBuildings, searchDldCommunities, searchDldZones } from '@/lib/db/dld-locations';
import { adminDealEditSchema, type AdminDealEditValues } from '@/lib/admin-deal-schemas';
import { PROPERTY_TYPES, type PropertyType } from '@/lib/db/deals';
import { formatGstDate, formatPublishedDate } from '@/lib/format';
import { getPgError } from '@/lib/pg-error';
import { useAdminDeal, useDealPhotos } from './AdminDealEditPage.data';

type ParseResult =
  | { ok: true; lat: number; lng: number }
  | { ok: false; reason: 'short_url' | 'unrecognized' };

function parseMapsCoords(input: string): ParseResult {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, reason: 'unrecognized' };
  // Detect shortened URLs that can't be parsed client-side
  if (/^https?:\/\/(maps\.app\.goo\.gl|goo\.gl)\//i.test(trimmed)) {
    return { ok: false, reason: 'short_url' };
  }
  // Format: @25.1972,55.2744 (Google Maps place URL)
  const atMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) return { ok: true, lat: Number(atMatch[1]), lng: Number(atMatch[2]) };
  // Format: ?q=25.1972,55.2744 or &query=25.1972,55.2744
  const qMatch = trimmed.match(/[?&](?:q|query)=(-?\d+\.\d+)[,+](-?\d+\.\d+)/);
  if (qMatch) return { ok: true, lat: Number(qMatch[1]), lng: Number(qMatch[2]) };
  // Format: "25.1972, 55.2744" (right-click copy from Google Maps)
  const rawMatch = trimmed.match(/^(-?\d+\.\d{3,}),\s*(-?\d+\.\d{3,})$/);
  if (rawMatch) return { ok: true, lat: Number(rawMatch[1]), lng: Number(rawMatch[2]) };
  // Format: !3d25.1972!4d55.2744 (Google Maps embed)
  const embedMatch = trimmed.match(/!3d(-?\d+\.\d+).*?!4d(-?\d+\.\d+)/);
  if (embedMatch) return { ok: true, lat: Number(embedMatch[1]), lng: Number(embedMatch[2]) };
  return { ok: false, reason: 'unrecognized' };
}

function MapsLinkInput({
  onCoordsFound,
  currentLat,
  currentLng,
  searchQuery,
}: {
  onCoordsFound: (lat: number, lng: number) => void;
  currentLat?: number | null;
  currentLng?: number | null;
  searchQuery?: string;
}) {
  const { t } = useTranslation();
  const [hint, setHint] = useState<'ok' | 'resolving' | 'error' | null>(null);

  const handleInput = async (text: string) => {
    const result = parseMapsCoords(text);
    if (result.ok) {
      onCoordsFound(result.lat, result.lng);
      setHint('ok');
      return;
    }

    if (result.reason === 'short_url') {
      setHint('resolving');
      try {
        const resolved = await resolveShortMapsUrl(text.trim());
        if (resolved) {
          onCoordsFound(resolved.lat, resolved.lng);
          setHint('ok');
        } else {
          setHint('error');
        }
      } catch {
        setHint('error');
      }
      return;
    }

    setHint(null);
  };

  return (
    <div>
      <label
        htmlFor="maps-link"
        style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}
      >
        {t('admin.dealEdit.fields.mapsLink')}
      </label>
      <input
        id="maps-link"
        type="text"
        className="m5-field__input"
        placeholder={t('admin.dealEdit.fields.mapsLinkPlaceholder')}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text');
          void handleInput(text);
        }}
        onChange={(e) => void handleInput(e.target.value)}
      />
      {searchQuery && (
        <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--c-link, #2563eb)' }}
          >
            {t('admin.dealEdit.fields.findOnMaps')}
          </a>
        </div>
      )}
      {hint === 'resolving' && (
        <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--c-muted)' }}>
          {t('admin.dealEdit.fields.mapsResolving')}
        </div>
      )}
      {hint === 'error' && (
        <div
          style={{
            marginTop: '0.25rem',
            fontSize: '0.75rem',
            color: 'var(--c-danger, #dc2626)',
          }}
        >
          {t('admin.dealEdit.fields.mapsShortUrlHint')}
        </div>
      )}
      {hint === 'ok' && (
        <div
          style={{
            marginTop: '0.25rem',
            fontSize: '0.75rem',
            color: 'var(--c-success, #16a34a)',
          }}
        >
          {t('admin.dealEdit.fields.mapsCoordsFilled')}
        </div>
      )}
      {(currentLat != null || currentLng != null) && (
        <div
          style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--c-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {currentLat}, {currentLng}
          {' · '}
          <a
            href={`https://www.google.com/maps?q=${currentLat},${currentLng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--c-link, #2563eb)' }}
          >
            {t('admin.dealEdit.fields.findOnMaps')}
          </a>
        </div>
      )}
    </div>
  );
}

async function resolveShortMapsUrl(url: string): Promise<{ lat: number; lng: number } | null> {
  const { env } = await import('@/lib/env');
  if (!env.supabase.url || !env.supabase.anonKey) return null;

  const resp = await fetch(`${env.supabase.url}/functions/v1/resolve-maps-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: env.supabase.anonKey,
      Authorization: `Bearer ${env.supabase.anonKey}`,
    },
    body: JSON.stringify({ url }),
  });

  if (!resp.ok) return null;
  const data = (await resp.json()) as { lat?: number; lng?: number };
  if (typeof data.lat === 'number' && typeof data.lng === 'number') {
    return { lat: data.lat, lng: data.lng };
  }
  return null;
}

function AdvisorSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>([]);

  useEffect(() => {
    import('@/lib/db/admin/advisors')
      .then(({ listAdvisors }) => listAdvisors())
      .then((advisors) =>
        setOptions(
          advisors
            .filter((a) => a.is_active)
            .map((a) => ({ id: a.id, label: `${a.first_name} ${a.last_name} — ${a.title}` })),
        ),
      )
      .catch(() => {});
  }, []);

  return (
    <div>
      <label
        htmlFor="advisor-select"
        style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}
      >
        Assigned advisor
      </label>
      <select
        id="advisor-select"
        className="m5-field__input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ maxWidth: '350px' }}
      >
        <option value="">— None —</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const TABS = ['type', 'property', 'price', 'media'] as const;
type TabKey = (typeof TABS)[number];

// Ordered mapping field → tab. Drives the auto-switch + focus behaviour when
// `form.handleSubmit` rejects with validation errors on inactive tabs (Spec
// UB14 — « jumplink + scroll + focus si erreur »). `meta_title` and
// `meta_description` live in the Price tab in the edit view (Spec §M10), so
// they map there too.
const FIELD_TAB_ORDER: ReadonlyArray<{ field: keyof AdminDealEditValues; tab: TabKey }> = [
  { field: 'type', tab: 'type' },
  { field: 'is_featured', tab: 'type' },
  { field: 'expiration_date', tab: 'type' },
  { field: 'internal_notes', tab: 'type' },
  { field: 'title', tab: 'property' },
  { field: 'description', tab: 'property' },
  { field: 'zone', tab: 'property' },
  { field: 'community', tab: 'property' },
  { field: 'building', tab: 'property' },
  { field: 'latitude', tab: 'property' },
  { field: 'longitude', tab: 'property' },
  { field: 'property_type', tab: 'property' },
  { field: 'area_sqft', tab: 'property' },
  { field: 'bedrooms', tab: 'property' },
  { field: 'bathrooms', tab: 'property' },
  { field: 'floor', tab: 'property' },
  { field: 'view', tab: 'property' },
  { field: 'handover', tab: 'property' },
  { field: 'asking_price', tab: 'price' },
  { field: 'original_price', tab: 'price' },
  { field: 'amount_paid', tab: 'price' },
  { field: 'meta_title', tab: 'price' },
  { field: 'meta_description', tab: 'price' },
];

function firstErrorLocation(
  errors: FieldErrors<AdminDealEditValues>,
): { tab: TabKey; field: keyof AdminDealEditValues } | null {
  for (const { field, tab } of FIELD_TAB_ORDER) {
    if (errors[field]) return { tab, field };
  }
  return null;
}

type ConfirmKind =
  | 'submitForReview'
  | 'publish'
  | 'refuse'
  | 'archive'
  | 'sold'
  | 'republish'
  | 'softDelete'
  | 'restore'
  | 'toggleFeaturedOn'
  | 'toggleFeaturedOff'
  | null;

const PRICE_FORMATTER = new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED',
  maximumFractionDigits: 0,
});

function STATUS_LABEL(status: AdminDealStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'validating':
      return 'Validating';
    case 'published':
      return 'Published';
    case 'archived':
      return 'Archived';
    case 'sold':
      return 'Sold';
    case 'refused':
      return 'Refused';
    case 'expired':
      return 'Expired';
  }
}

function dealToFormValues(deal: AdminDeal): AdminDealEditValues {
  return {
    type: deal.type === 'off_plan' ? 'off_plan' : 'ready',
    is_featured: deal.is_featured,
    expiration_date: deal.expiration_date,
    internal_notes: deal.internal_notes,
    advisor_id: deal.advisor_id ?? null,
    title: deal.title,
    description: deal.description,
    zone: deal.zone,
    community: deal.community,
    building: deal.building,
    // ADR-055 — canonical keys travel with the form (hidden fields). `??`
    // guards older fixtures that predate the location-key columns.
    zone_key: deal.zone_key ?? null,
    community_key: deal.community_key ?? null,
    building_key: deal.building_key ?? null,
    // `==` on purpose so we catch both `null` (DB row) and `undefined`
    // (older fixtures that predate the M4 columns). Without this guard, a
    // missing column would land NaN in the form state and trip the Zod
    // refine (Zod 4 rejects NaN at the union level — see schema notes).
    latitude: deal.latitude == null ? null : Number(deal.latitude),
    longitude: deal.longitude == null ? null : Number(deal.longitude),
    property_type: deal.property_type as PropertyType,
    area_sqft: deal.area_sqft,
    bedrooms: deal.bedrooms,
    bathrooms: deal.bathrooms,
    floor: deal.floor,
    view: deal.view,
    handover: deal.handover,
    asking_price: Number(deal.asking_price),
    original_price: deal.original_price === null ? null : Number(deal.original_price),
    amount_paid: deal.amount_paid === null ? null : Number(deal.amount_paid),
    meta_title: deal.meta_title,
    meta_description: deal.meta_description,
  };
}

/**
 * ADR-055 Lot 3 — non-blocking DLD identity diagnostic. Shows HOW the
 * benchmark engine resolves this deal (exact building / approved alias /
 * project group / community fallback / zone fallback / unresolved).
 * Purely informative — publication is never gated on it. `refreshKey`
 * bumps after every save so the panel reflects the latest keys.
 */
function DealIdentityDiagnosticPanel({
  dealId,
  refreshKey,
}: {
  dealId: string;
  refreshKey: number;
}) {
  const { t } = useTranslation();
  const [diag, setDiag] = useState<DealIdentityDiagnostic | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    diagnoseDealIdentity(dealId)
      .then((d) => {
        if (!cancelled) {
          setDiag(d);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshKey]);

  if (failed) {
    return (
      <p className="admin-text-muted" data-testid="dld-diagnostic-error">
        {t('admin.dealEdit.dldDiagnostic.error')}
      </p>
    );
  }
  if (!diag) return null;

  const resolved = diag.resolution_label !== 'unresolved';
  return (
    <div className="admin-card admin-card--pad" data-testid="dld-diagnostic">
      <h3 className="admin-subsection-title">{t('admin.dealEdit.dldDiagnostic.title')}</h3>
      <p className="admin-form-section__desc">{t('admin.dealEdit.dldDiagnostic.desc')}</p>
      <p>
        <span
          className={`admin-badge ${resolved ? 'admin-badge--success' : 'admin-badge--warning'}`}
          data-testid="dld-diagnostic-label"
        >
          {t(`admin.dealEdit.dldDiagnostic.labels.${diag.resolution_label}`)}
        </span>
      </p>
      <dl className="admin-meta-list">
        {diag.canonical_building_key ? (
          <>
            <dt>{t('admin.dealEdit.dldDiagnostic.building')}</dt>
            <dd>{diag.canonical_building_key}</dd>
          </>
        ) : null}
        {diag.group_key ? (
          <>
            <dt>{t('admin.dealEdit.dldDiagnostic.group')}</dt>
            <dd>
              {diag.group_key} ({diag.group_building_keys.length})
            </dd>
          </>
        ) : null}
        {diag.effective_community_key ? (
          <>
            <dt>{t('admin.dealEdit.dldDiagnostic.effectiveCommunity')}</dt>
            <dd>{diag.effective_community_key}</dd>
          </>
        ) : null}
        {diag.effective_zone_key ? (
          <>
            <dt>{t('admin.dealEdit.dldDiagnostic.effectiveZone')}</dt>
            <dd>{diag.effective_zone_key}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

/** Map a persisted `deals.dld_scope` to its short admin label key. */
function dldScopeFamilyKey(scope: string | null | undefined): string | null {
  if (!scope) return null;
  if (scope.startsWith('same_building')) return 'building';
  if (scope.startsWith('same_community')) return 'community';
  if (scope === 'broader_market') return 'broader';
  if (scope === 'off_plan') return 'offPlan';
  return 'insufficient';
}

export function AdminDealEditPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const dealQuery = useAdminDeal(id);

  const [tab, setTab] = useState<TabKey>('type');
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [refuseMotif, setRefuseMotif] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // ADR-055 — bump to re-run the DLD identity diagnostic after each save.
  const [diagRefreshKey, setDiagRefreshKey] = useState(0);
  const [retryingPhotoCopy, setRetryingPhotoCopy] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(0);
  const photosState = useDealPhotos(id);

  const role = auth.status === 'authenticated' ? auth.profile.role : null;
  const isSuperAdmin = role === 'super_admin';

  // Memoise the Zod schema + resolver. Without this, every parent re-render
  // (every keystroke, every tab switch, every photosState mutation) builds a
  // fresh composite Zod object and a fresh resolver — observable render churn
  // on heavier tabs (Price, Property). `t` is identity-stable so `[t]` is the
  // only dep we need.
  const schema = useMemo(() => adminDealEditSchema(t), [t]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const form = useForm<AdminDealEditValues>({
    resolver,
    mode: 'onSubmit',
  });

  // Track zone + community values so the building / community comboboxes
  // can narrow their RPC search context (3-level cascading combobox PR-3).
  // ADR-055 Lot 3 — cascade filters use the SELECTED canonical keys, never a
  // lowercased label guess; NULL key (free text) simply removes the filter.
  const zoneKeyValue = useWatch({ control: form.control, name: 'zone_key' });
  const communityKeyValue = useWatch({ control: form.control, name: 'community_key' });

  const searchZoneOptions = useCallback(async (q: string): Promise<LocationOption[]> => {
    const rows = await searchDldZones(q);
    return rows.map((r) => ({
      key: r.zone_key,
      displayName: r.display_name,
      count: r.transaction_count,
    }));
  }, []);

  const searchCommunityOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const rows = await searchDldCommunities(q, zoneKeyValue ?? null);
      return rows.map((r) => ({
        key: r.community_key,
        displayName: r.display_name,
        secondary: r.zone ?? undefined,
        count: r.transaction_count,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [zoneKeyValue],
  );

  const searchBuildingOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const rows = await searchDldBuildings(q, communityKeyValue ?? null, zoneKeyValue ?? null);
      return rows.map((r) => ({
        key: r.building_key,
        displayName: r.display_name,
        secondary: r.community_display_name ?? r.community_key ?? undefined,
        count: r.transaction_count,
        communityKey: r.community_key ?? undefined,
        communityDisplayName: r.community_display_name ?? undefined,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [communityKeyValue, zoneKeyValue],
  );

  // ADR-055 — hierarchical key clearing: a free-text edit on a level's label
  // invalidates its key AND every child key (zone → community → building).
  // commitOption fires onChange(label) BEFORE onCommitOption(option), so a
  // combobox pick clears here then immediately re-sets the canonical keys.
  const clearKeysFrom = useCallback(
    (level: 'zone' | 'community' | 'building') => {
      if (level === 'zone') form.setValue('zone_key', null, { shouldDirty: true });
      if (level !== 'building') form.setValue('community_key', null, { shouldDirty: true });
      form.setValue('building_key', null, { shouldDirty: true });
    },
    [form],
  );

  const deal = dealQuery.status === 'success' ? dealQuery.deal : null;
  // We can't put `deal` itself as a dep — `useAdminDeal` returns a fresh
  // object on every refetch, so a deep-deps effect would loop infinitely
  // through form.reset → new render → new ref → reset again. Track id +
  // updated_at instead so we only reset on a genuinely different snapshot.
  const dealId = deal?.id;
  const dealUpdatedAt = deal?.updated_at;

  useEffect(() => {
    if (deal) {
      form.reset(dealToFormValues(deal));
    }
    // form is stable — listing it would still re-trigger; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, dealUpdatedAt]);

  const askingPriceLocked = useMemo(() => {
    if (!deal) return false;
    if (deal.status !== 'published') return false;
    return !isSuperAdmin;
  }, [deal, isSuperAdmin]);

  // Options for the harmonised Property type <Select>. Memoised so RHF's
  // identity-stable subscribers don't re-render on every keystroke.
  const propertyTypeOptions = useMemo<SelectOption[]>(
    () =>
      PROPERTY_TYPES.map((pt) => ({
        value: pt,
        label: t(`admin.dealEdit.propertyTypeOpts.${pt}`),
      })),
    [t],
  );

  // RHF onInvalid: toast, jump to first offending tab, focus first offending
  // field (Spec UB14). Custom <Select> isn't registered with RHF, so
  // `setFocus` may throw silently — swallowed.
  const handleInvalid = (errors: FieldErrors<AdminDealEditValues>) => {
    const count = Object.keys(errors).length;
    toast.error(t('admin.dealEdit.validation.missingFieldsToast', { count }));
    const found = firstErrorLocation(errors);
    if (!found) return;
    setTab(found.tab);
    setTimeout(() => {
      try {
        form.setFocus(found.field);
      } catch {
        // Custom select / segmented control — no DOM input registered.
      }
    }, 50);
  };

  const handleSave = form.handleSubmit(async (values) => {
    if (!deal) return;
    setSubmitting(true);
    try {
      await updateAdminDeal(deal.id, {
        title: values.title,
        description: values.description,
        zone: values.zone,
        community: values.community,
        building: values.building,
        zone_key: values.zone_key,
        community_key: values.community_key,
        building_key: values.building_key,
        latitude: values.latitude,
        longitude: values.longitude,
        type: values.type,
        property_type: values.property_type,
        area_sqft: values.area_sqft,
        bedrooms: values.bedrooms,
        bathrooms: values.bathrooms,
        floor: values.floor,
        view: values.view,
        handover: values.handover,
        asking_price: askingPriceLocked ? deal.asking_price : values.asking_price,
        original_price: values.original_price,
        amount_paid: values.amount_paid,
        meta_title: values.meta_title,
        meta_description: values.meta_description,
        expiration_date: values.expiration_date,
        internal_notes: values.internal_notes,
        advisor_id: values.advisor_id || null,
      });
      toast.success(t('admin.dealEdit.savedToast'));
      setDiagRefreshKey((k) => k + 1);
      dealQuery.refetch();
    } catch (err) {
      const pg = getPgError(err);
      if (pg.code === '42501' && pg.message.toLowerCase().includes('asking_price')) {
        toast.error(t('admin.dealEdit.errorAskingPricePostPublishedToast'));
      } else {
        toast.error(t('admin.dealEdit.errorSaveToast'));
      }
    } finally {
      setSubmitting(false);
    }
  }, handleInvalid);

  async function runTransition(kind: ConfirmKind) {
    if (!deal || !kind) return;
    setSubmitting(true);
    try {
      switch (kind) {
        case 'submitForReview':
          await transitionDealStatus({ dealId: deal.id, toStatus: 'validating' });
          toast.success(t('admin.dealEdit.transitions.submittedToast'));
          break;
        case 'publish':
          await publishDeal(deal.id);
          toast.success(t('admin.dealEdit.transitions.publishedToast'));
          break;
        case 'refuse':
          await transitionDealStatus({
            dealId: deal.id,
            toStatus: 'refused',
            motif: refuseMotif,
          });
          toast.success(t('admin.dealEdit.transitions.refusedToast'));
          break;
        case 'archive':
          await transitionDealStatus({ dealId: deal.id, toStatus: 'archived' });
          toast.success(t('admin.dealEdit.transitions.archivedToast'));
          break;
        case 'sold':
          await transitionDealStatus({ dealId: deal.id, toStatus: 'sold' });
          toast.success(t('admin.dealEdit.transitions.soldToast'));
          break;
        case 'republish': {
          const restored = await republishDeal(deal.id);
          // A past expiration_date does not block the republication, but the
          // nightly auto_expire_deals CRON undoes it — warn instead of
          // reporting a plain success (ADR-059).
          if (isExpirationPast(restored.expiration_date)) {
            toast.warning(t('admin.dealEdit.transitions.republishedExpiredToast'));
          } else {
            toast.success(t('admin.dealEdit.transitions.republishedToast'));
          }
          break;
        }
        case 'softDelete':
          await softDeleteDeal(deal.id);
          toast.success(t('admin.dealEdit.transitions.softDeletedToast'));
          break;
        case 'restore':
          await restoreDeal(deal.id);
          toast.success(t('admin.dealEdit.transitions.restoredToast'));
          break;
      }
      dealQuery.refetch();
      setConfirm(null);
      setRefuseMotif('');
    } catch (err) {
      const pg = getPgError(err);
      const msg = pg.message.toLowerCase();
      if (pg.code === '55000' && msg.includes('soft-deleted')) {
        toast.error(t('admin.dealEdit.errorRepublishDeletedToast'));
      } else if (pg.code === '55000') {
        // republish_deal saw a status other than `archived` — someone else
        // already republished it, or it moved on. Refresh so the UI stops
        // offering an action the row no longer supports.
        toast.error(t('admin.dealEdit.errorRepublishNotArchivedToast'));
      } else if (pg.code === '42501' && kind === 'republish') {
        toast.error(t('admin.dealEdit.errorRepublishForbiddenToast'));
      } else if (msg.includes('at least one photo')) {
        toast.error(t('admin.dealEdit.errorPhotosRequiredToast'));
      } else if (msg.includes('asking_price') && msg.includes('50000')) {
        toast.error(t('admin.dealEdit.errorAskingPriceTooLowToast'));
      } else if (msg.includes('draft placeholder slug')) {
        toast.error(t('admin.dealEdit.errorDraftSlugToast'));
      } else if (msg.includes('qa/test artifact')) {
        toast.error(t('admin.dealEdit.errorQaTitleToast'));
      } else {
        toast.error(t('admin.dealEdit.errorTransitionToast'));
      }
      // A 55000 means the row moved under us: close the modal and reload so
      // the lifecycle block stops offering a transition the deal no longer
      // supports. Every other error keeps the modal open for a retry.
      if (pg.code === '55000') {
        setConfirm(null);
        dealQuery.refetch();
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFeaturedToggle(next: boolean) {
    if (!deal) return;
    setSubmitting(true);
    try {
      await toggleDealFeatured(deal.id, next);
      toast.success(
        next
          ? t('admin.dealEdit.transitions.featuredOnToast')
          : t('admin.dealEdit.transitions.featuredOffToast'),
      );
      dealQuery.refetch();
      setConfirm(null);
    } catch {
      toast.error(t('admin.dealEdit.errorTransitionToast'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRetryPhotoCopy() {
    if (!deal) return;
    setRetryingPhotoCopy(true);
    toast.message(t('admin.dealEdit.photoCopy.retryingToast'));
    try {
      const result = await retryDealPhotoCopy(deal.id);
      if (result.photos_copy_status === 'ok') {
        toast.success(t('admin.dealEdit.photoCopy.retrySuccessToast'));
      } else if (result.photos_copy_status === 'none') {
        toast.success(t('admin.dealEdit.photoCopy.retryNoneSuccessToast'));
      } else {
        toast.error(t('admin.dealEdit.photoCopy.retryFailedToast'));
      }
      dealQuery.refetch();
      photosState.refetch();
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      if (code === 'not_retryable') {
        toast.error(t('admin.dealEdit.photoCopy.retryNotRetryableToast'));
      } else if (code === 'submission_not_found') {
        toast.error(t('admin.dealEdit.photoCopy.retryNoSubmissionToast'));
      } else {
        toast.error(t('admin.dealEdit.photoCopy.retryFailedToast'));
      }
    } finally {
      setRetryingPhotoCopy(false);
    }
  }

  // BO-3: pending photo uploads count as "dirty" so leaving the page
  // mid-upload triggers the unsaved-changes confirm dialog.
  const blocker = useFormDirty({
    isDirty: form.formState.isDirty || pendingUploads > 0,
    isPending: submitting,
  });

  if (dealQuery.status === 'loading') {
    return (
      <div className="admin-state-block" role="status" aria-busy="true">
        <Skeleton width="30%" height={20} />
        <div style={{ height: 20 }} />
        <Skeleton width="100%" height={120} />
        <div style={{ height: 16 }} />
        <Skeleton width="100%" height={220} />
      </div>
    );
  }
  if (dealQuery.status === 'not_found') {
    return (
      <div className="admin-state-block" role="alert">
        {t('admin.dealEdit.notFound')}
      </div>
    );
  }
  if (dealQuery.status === 'error') {
    return (
      <div className="admin-state-block" role="alert">
        {t('admin.dealEdit.errorLoading')}
      </div>
    );
  }

  // dealQuery.status === 'success'
  const currentDeal = dealQuery.deal;
  const deletedAt = currentDeal.deleted_at !== null;

  const transitionsAvailable = (() => {
    if (deletedAt) {
      return { restore: isSuperAdmin };
    }
    switch (currentDeal.status) {
      case 'draft':
        return {
          submitForReview: true,
          softDelete: isSuperAdmin,
        };
      case 'validating':
        return {
          publish: isSuperAdmin,
          refuse: isSuperAdmin,
          softDelete: isSuperAdmin,
        };
      case 'published':
        return {
          archive: true,
          sold: true,
          softDelete: isSuperAdmin,
          featured: isSuperAdmin,
        };
      // D3 (Modele V1.2 §10.1) — `archived` is the only terminal status with
      // a way back: super_admin can republish the existing row. `sold`,
      // `refused` and `expired` stay closed.
      case 'archived':
        return {
          republish: isSuperAdmin,
          softDelete: isSuperAdmin,
        };
      case 'sold':
      case 'refused':
      case 'expired':
        return {
          softDelete: isSuperAdmin,
        };
      default:
        return {};
    }
  })() as Record<string, boolean | undefined>;

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">
            {t('admin.dealEdit.title')} · {currentDeal.title}
          </h1>
          <div className="admin-head__sub">
            {currentDeal.reference_id} · {STATUS_LABEL(currentDeal.status)}
            {deletedAt ? ' · 🗑️' : ''}
          </div>
        </div>
        <div className="admin-head__actions">
          {currentDeal.status === 'published' ? (
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              onClick={() => navigate(`/deals/${currentDeal.slug}`)}
            >
              {t('admin.dealEdit.actions.preview')}
            </button>
          ) : null}
          <button
            type="button"
            className="admin-btn"
            disabled={submitting || deletedAt}
            onClick={handleSave}
          >
            {t('admin.dealEdit.actions.save')}
          </button>
        </div>
      </header>

      {currentDeal.photos_copy_status === 'failed' ? (
        <div className="admin-photo-copy-alert" role="alert">
          <div className="admin-photo-copy-alert__content">
            <div className="admin-photo-copy-alert__title">
              {t('admin.dealEdit.photoCopy.failedAlert.title')}
            </div>
            <div className="admin-photo-copy-alert__body">
              {t('admin.dealEdit.photoCopy.failedAlert.body')}
            </div>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn--outline"
            disabled={retryingPhotoCopy || deletedAt}
            onClick={() => void handleRetryPhotoCopy()}
          >
            {retryingPhotoCopy
              ? t('admin.dealEdit.photoCopy.retryingToast')
              : t('admin.dealEdit.photoCopy.retryButton')}
          </button>
        </div>
      ) : null}

      <div className="admin-tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`admin-tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(`admin.dealEdit.tabs.${key}`)}
          </button>
        ))}
      </div>

      <form onSubmit={handleSave} noValidate>
        {tab === 'type' && (
          <div className="admin-form">
            <div style={{ marginBottom: 24 }}>
              <div className="admin-fieldset-label">{t('admin.dealEdit.fields.type')}</div>
              <div className="admin-segmented">
                {(['ready', 'off_plan'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`admin-segmented__btn${form.watch('type') === opt ? ' is-active' : ''}`}
                    onClick={() => form.setValue('type', opt, { shouldDirty: true })}
                  >
                    {t(
                      opt === 'ready'
                        ? 'admin.dealEdit.fields.ready'
                        : 'admin.dealEdit.fields.offPlan',
                    )}
                  </button>
                ))}
              </div>
              {form.formState.errors.type ? (
                <div className="admin-form__error">{form.formState.errors.type.message}</div>
              ) : null}
            </div>

            <div style={{ marginBottom: 24 }}>
              <div className="admin-fieldset-label">{t('admin.dealEdit.fields.featured')}</div>
              <Checkbox
                label={`${t('admin.dealEdit.fields.featuredHint')} ${currentDeal.status !== 'published' ? t('admin.dealEdit.fields.featuredRequires') : ''}`}
                checked={currentDeal.is_featured}
                disabled={
                  currentDeal.status !== 'published' || !isSuperAdmin || submitting || deletedAt
                }
                name="is-featured"
                onChange={(event) =>
                  setConfirm(event.target.checked ? 'toggleFeaturedOn' : 'toggleFeaturedOff')
                }
              />
            </div>

            <TextField
              label={t('admin.dealEdit.fields.expiry')}
              type="date"
              helper={t('admin.dealEdit.fields.expiryHint')}
              {...form.register('expiration_date')}
            />
            <Textarea
              label={t('admin.dealEdit.fields.internalNotes')}
              rows={3}
              {...form.register('internal_notes')}
            />
            <AdvisorSelect
              value={form.watch('advisor_id') ?? null}
              onChange={(id) => form.setValue('advisor_id', id, { shouldDirty: true })}
            />
          </div>
        )}

        {tab === 'property' && (
          <div className="admin-form">
            <TextField
              label={t('admin.dealEdit.fields.title')}
              required
              error={form.formState.errors.title?.message}
              {...form.register('title')}
            />
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
                {t('admin.dealEdit.fields.description')}
              </label>
              <HtmlEditor
                value={form.watch('description') ?? ''}
                onChange={(html) => form.setValue('description', html, { shouldDirty: true })}
                ariaLabel={t('admin.dealEdit.fields.description')}
                style={{ minHeight: '180px' }}
              />
            </div>
            <div className="admin-form__row-grid">
              <Controller
                control={form.control}
                name="zone"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="zone"
                    label={t('admin.dealEdit.fields.zone')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('zone');
                    }}
                    onSearch={searchZoneOptions}
                    onCommitOption={(opt) => {
                      form.setValue('zone_key', opt.key, { shouldDirty: true });
                    }}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="community"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="community"
                    label={t('admin.dealEdit.fields.community')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('community');
                    }}
                    onSearch={searchCommunityOptions}
                    onCommitOption={(opt) => {
                      form.setValue('community_key', opt.key, { shouldDirty: true });
                      if (opt.zoneKey) {
                        form.setValue('zone_key', opt.zoneKey, { shouldDirty: true });
                      }
                      if (opt.zoneDisplayName) {
                        form.setValue('zone', opt.zoneDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="building"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="building"
                    label={t('admin.dealEdit.fields.building')}
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('building');
                    }}
                    onSearch={searchBuildingOptions}
                    onCommitOption={(opt) => {
                      // The combobox only ever offers dld_buildings rows, so a
                      // text matching only a project GROUP can never assign a
                      // specific tower here (group resolution is compute-time).
                      form.setValue('building_key', opt.key, { shouldDirty: true });
                      if (opt.communityKey) {
                        form.setValue('community_key', opt.communityKey, { shouldDirty: true });
                      }
                      if (opt.communityDisplayName) {
                        form.setValue('community', opt.communityDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                      if (opt.zoneKey) {
                        form.setValue('zone_key', opt.zoneKey, { shouldDirty: true });
                      }
                      if (opt.zoneDisplayName) {
                        form.setValue('zone', opt.zoneDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <MapsLinkInput
                onCoordsFound={(lat, lng) => {
                  form.setValue('latitude', lat, { shouldDirty: true });
                  form.setValue('longitude', lng, { shouldDirty: true });
                }}
                currentLat={form.watch('latitude')}
                currentLng={form.watch('longitude')}
                searchQuery={[form.watch('building'), form.watch('community'), 'Dubai']
                  .filter((s): s is string => !!s && s.trim().length > 0)
                  .join(', ')}
              />
              <input
                type="hidden"
                {...form.register('latitude', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <input
                type="hidden"
                {...form.register('longitude', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              {deal ? (
                <DealIdentityDiagnosticPanel dealId={deal.id} refreshKey={diagRefreshKey} />
              ) : null}
              <div>
                <div className="admin-fieldset-label">
                  {t('admin.dealEdit.fields.propertyType')}
                </div>
                <Select
                  ariaLabel={t('admin.dealEdit.fields.propertyType')}
                  options={propertyTypeOptions}
                  value={form.watch('property_type') ?? 'apartment'}
                  onChange={(v) =>
                    form.setValue('property_type', (v ?? 'apartment') as PropertyType, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  className="admin-select-block"
                />
                {form.formState.errors.property_type ? (
                  <div className="admin-form__error">
                    {form.formState.errors.property_type.message}
                  </div>
                ) : null}
              </div>
              <TextField
                label={t('admin.dealEdit.fields.bedrooms')}
                type="number"
                error={form.formState.errors.bedrooms?.message}
                {...form.register('bedrooms', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.bathrooms')}
                type="number"
                error={form.formState.errors.bathrooms?.message}
                {...form.register('bathrooms', {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField
                label={t('admin.dealEdit.fields.area')}
                type="number"
                error={form.formState.errors.area_sqft?.message}
                {...form.register('area_sqft', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.floor')}
                type="number"
                {...form.register('floor', {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField label={t('admin.dealEdit.fields.view')} {...form.register('view')} />
              <TextField
                label={t('admin.dealEdit.fields.handover')}
                {...form.register('handover')}
              />
            </div>
          </div>
        )}

        {tab === 'price' && (
          <div className="admin-form">
            <div className="admin-form__row-grid" style={{ marginBottom: 24 }}>
              <TextField
                label={t('admin.dealEdit.fields.askingPrice')}
                type="number"
                required
                disabled={askingPriceLocked}
                helper={
                  askingPriceLocked
                    ? t('admin.dealEdit.fields.askingPriceLockedHint')
                    : t('admin.dealEdit.fields.askingPriceMinHint')
                }
                error={form.formState.errors.asking_price?.message}
                {...form.register('asking_price', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.originalPrice')}
                type="number"
                {...form.register('original_price', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField
                label={t('admin.dealEdit.fields.amountPaid')}
                type="number"
                {...form.register('amount_paid', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
            </div>

            <div className="admin-dld-card">
              <div className="admin-dld-card__kicker">{t('admin.dealEdit.dld.kicker')}</div>
              {!currentDeal.dld_computed_at ? (
                <div className="admin-text-muted" style={{ marginBottom: 0 }}>
                  {t('admin.dealEdit.dld.notComputed')}
                </div>
              ) : (
                <>
                  <div className="admin-dld-grid">
                    <div>
                      <div className="admin-dld-grid__label">
                        {t('admin.dealEdit.dld.transactionsUsed')}
                      </div>
                      <div className="admin-dld-grid__value">
                        {currentDeal.dld_transactions_count ?? '—'}
                      </div>
                    </div>
                    <div>
                      <div className="admin-dld-grid__label">
                        {t('admin.dealEdit.dld.benchmark')}
                      </div>
                      <div className="admin-dld-grid__value">
                        {currentDeal.reference_price !== null
                          ? PRICE_FORMATTER.format(Number(currentDeal.reference_price))
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="admin-dld-grid__label">
                        {t('admin.dealEdit.dld.discount')}
                      </div>
                      <div
                        className={`admin-dld-grid__value${
                          currentDeal.discount_pct !== null && Number(currentDeal.discount_pct) > 0
                            ? ' admin-dld-grid__value--positive'
                            : ''
                        }`}
                      >
                        {currentDeal.discount_pct !== null ? `${currentDeal.discount_pct}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="admin-dld-grid__label">
                        {t('admin.dealEdit.dld.scopeConfidence')}
                      </div>
                      <div className="admin-dld-grid__value">
                        {(() => {
                          const fam = dldScopeFamilyKey(currentDeal.dld_scope);
                          const parts = [
                            fam ? t(`admin.dealEdit.dld.scope.${fam}`) : null,
                            currentDeal.dld_confidence
                              ? t(`benchmark.confidence.${currentDeal.dld_confidence}`)
                              : null,
                          ].filter(Boolean);
                          return parts.length > 0 ? parts.join(' · ') : '—';
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="admin-text-muted" style={{ marginBottom: 0 }}>
                    {t('admin.dealEdit.dld.computed', {
                      date: formatPublishedDate(currentDeal.dld_computed_at, i18n.language),
                    })}
                  </div>
                </>
              )}
            </div>

            <TextField
              label={t('admin.dealEdit.fields.metaTitle')}
              {...form.register('meta_title')}
            />
            <Textarea
              label={t('admin.dealEdit.fields.metaDescription')}
              rows={3}
              {...form.register('meta_description')}
            />
          </div>
        )}

        {tab === 'media' && (
          <div className="admin-form">
            <DealPhotoUploader
              dealId={currentDeal.id}
              currentPhotoCount={photosState.photos.length}
              onPhotoUploaded={(photo) => photosState.setPhotos((prev) => [...prev, photo])}
              onActiveUploadsChange={setPendingUploads}
            />
            <DealPhotoGallery
              dealId={currentDeal.id}
              photos={photosState.photos}
              dealStatus={currentDeal.status}
              onPhotosChange={photosState.setPhotos}
            />
          </div>
        )}
      </form>

      <section className="admin-form" aria-label={t('admin.dealEdit.lifecycle.label')}>
        <div className="admin-fieldset-label">{t('admin.dealEdit.lifecycle.label')}</div>
        <div className="admin-lifecycle-actions">
          {transitionsAvailable.submitForReview ? (
            <button
              type="button"
              className="admin-btn"
              disabled={submitting}
              onClick={() => setConfirm('submitForReview')}
            >
              {t('admin.dealEdit.lifecycle.submitForReview')}
            </button>
          ) : null}
          {transitionsAvailable.publish ? (
            <button
              type="button"
              className="admin-btn"
              disabled={submitting}
              onClick={() => setConfirm('publish')}
            >
              {t('admin.dealEdit.lifecycle.publish')}
            </button>
          ) : null}
          {transitionsAvailable.refuse ? (
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              disabled={submitting}
              onClick={() => setConfirm('refuse')}
            >
              {t('admin.dealEdit.lifecycle.refuse')}
            </button>
          ) : null}
          {transitionsAvailable.archive ? (
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              disabled={submitting}
              onClick={() => setConfirm('archive')}
            >
              {t('admin.dealEdit.lifecycle.archive')}
            </button>
          ) : null}
          {transitionsAvailable.sold ? (
            <button
              type="button"
              className="admin-btn admin-btn--outline"
              disabled={submitting}
              onClick={() => setConfirm('sold')}
            >
              {t('admin.dealEdit.lifecycle.markSold')}
            </button>
          ) : null}
          {transitionsAvailable.republish ? (
            <button
              type="button"
              className="admin-btn"
              disabled={submitting}
              onClick={() => setConfirm('republish')}
            >
              {t('admin.dealEdit.lifecycle.republish')}
            </button>
          ) : null}
          {transitionsAvailable.softDelete ? (
            <button
              type="button"
              className="admin-btn admin-btn--danger"
              disabled={submitting}
              onClick={() => setConfirm('softDelete')}
            >
              {t('admin.dealEdit.lifecycle.softDelete')}
            </button>
          ) : null}
          {transitionsAvailable.restore ? (
            <button
              type="button"
              className="admin-btn"
              disabled={submitting}
              onClick={() => setConfirm('restore')}
            >
              {t('admin.dealEdit.lifecycle.restore')}
            </button>
          ) : null}
        </div>
      </section>

      {confirm &&
      confirm !== 'refuse' &&
      confirm !== 'republish' &&
      confirm !== 'toggleFeaturedOn' &&
      confirm !== 'toggleFeaturedOff' ? (
        <ConfirmModal
          title={t(`admin.dealEdit.confirm.${confirm}.title`)}
          body={t(`admin.dealEdit.confirm.${confirm}.body`)}
          confirmLabel={t(`admin.dealEdit.lifecycle.${confirmKindLabel(confirm)}`)}
          danger={confirm === 'softDelete'}
          onConfirm={() => void runTransition(confirm)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm === 'toggleFeaturedOn' || confirm === 'toggleFeaturedOff' ? (
        <ConfirmModal
          title={t(`admin.dealEdit.confirm.${confirm}.title`)}
          body={t(`admin.dealEdit.confirm.${confirm}.body`)}
          onConfirm={() => void handleFeaturedToggle(confirm === 'toggleFeaturedOn')}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {blocker.state === 'blocked' ? (
        <ConfirmModal
          title={t('admin.dealEdit.confirm.discardChanges.title')}
          body={t('admin.dealEdit.confirm.discardChanges.body')}
          confirmLabel={t('admin.dealEdit.confirm.discardChanges.confirmLabel')}
          danger
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      ) : null}

      {confirm === 'republish' ? (
        <ConfirmModal
          title={t('admin.dealEdit.confirm.republish.title')}
          body={
            <span>
              <strong>{currentDeal.reference_id}</strong> — {currentDeal.title}
              <br />
              {t('admin.dealEdit.confirm.republish.body')}
              {isExpirationPast(currentDeal.expiration_date) ? (
                <strong style={{ display: 'block', marginTop: 10, color: 'var(--ddm-warning)' }}>
                  {t('admin.dealEdit.confirm.republish.expiredWarning', {
                    date: formatGstDate(currentDeal.expiration_date),
                  })}
                </strong>
              ) : null}
            </span>
          }
          confirmLabel={t('admin.dealEdit.lifecycle.republish')}
          busy={submitting}
          onConfirm={() => {
            if (!submitting) void runTransition('republish');
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm === 'refuse' ? (
        <ConfirmModal
          title={t('admin.dealEdit.confirm.refuse.title')}
          body={
            <span>
              {t('admin.dealEdit.confirm.refuse.body')}
              <br />
              <textarea
                value={refuseMotif}
                onChange={(event) => setRefuseMotif(event.target.value)}
                rows={4}
                placeholder={t('admin.dealEdit.refuseMotif.placeholder')}
                aria-label={t('admin.dealEdit.refuseMotif.label')}
                style={{ width: '100%', marginTop: 12, fontFamily: 'inherit' }}
              />
              <small style={{ display: 'block', marginTop: 6 }}>
                {t('admin.dealEdit.refuseMotif.hint', { count: refuseMotif.length })}
              </small>
            </span>
          }
          confirmLabel={t('admin.dealEdit.lifecycle.refuse')}
          danger
          onConfirm={() => void runTransition('refuse')}
          onCancel={() => {
            setConfirm(null);
            setRefuseMotif('');
          }}
          requireType={refuseMotif.length >= 20 ? undefined : '__INVALID__'}
        />
      ) : null}
    </>
  );
}

function confirmKindLabel(
  kind: Exclude<
    ConfirmKind,
    null | 'refuse' | 'republish' | 'toggleFeaturedOn' | 'toggleFeaturedOff'
  >,
): string {
  switch (kind) {
    case 'submitForReview':
      return 'submitForReview';
    case 'publish':
      return 'publish';
    case 'archive':
      return 'archive';
    case 'sold':
      return 'markSold';
    case 'softDelete':
      return 'softDelete';
    case 'restore':
      return 'restore';
  }
}
